import { DateTime } from "luxon";
import { TEAM_SECTIONS } from "../../components/team/config";
import { getWeekStart } from "../../utils/dateUtils";
import type { EmploymentType } from "../../utils/models";

/** Base pay-period assumption: drives the default range length and the weekly-averaging divisor. */
export const PAY_PERIOD_DAYS = 7;

/**
 * Default leakage-report range: the current pay-period week anchored to the department's
 * week_start_day. The end lands on the SAME weekday as the start, one week out (e.g. Sun→Sun) —
 * an exclusive week boundary, so the period is exactly PAY_PERIOD_DAYS. `now` defaults to the
 * current instant, so callers only need to pass the department week start.
 */
export function getDefaultLeakageRange(weekStartDay: number, now: DateTime = DateTime.now()): { startDate: DateTime; endDate: DateTime } {
	const startDate = getWeekStart(now, weekStartDay);
	const endDate = startDate.plus({ days: PAY_PERIOD_DAYS });
	return { startDate, endDate };
}

/** Earliest allowed end date for a given start: one full week out, on the same weekday (Sun→Sun). */
export function minEndDate(startDate: DateTime): DateTime {
	return startDate.plus({ days: PAY_PERIOD_DAYS });
}

/**
 * Whether the end completes a WHOLE number of pay-period weeks from the start (at least one), with
 * the end on the same weekday as the start (an exclusive week boundary). Used to disable invalid
 * end dates in the picker — e.g. a Sunday start only allows the following Sundays. The exclusive
 * boundary keeps each week exactly PAY_PERIOD_DAYS long; the API receives the inclusive last day.
 */
export function isValidWholeWeekEnd(startDate: DateTime, endDate: DateTime): boolean {
	const diffDays = Math.round(endDate.startOf("day").diff(startDate.startOf("day"), "days").days);
	return diffDays >= PAY_PERIOD_DAYS && diffDays % PAY_PERIOD_DAYS === 0;
}

/** Parse an ISO date from a URL search param; returns undefined when missing or invalid. */
export function parseRangeDate(param: string | null): DateTime | undefined {
	if (!param) return undefined;
	const parsed = DateTime.fromISO(param);
	return parsed.isValid ? parsed : undefined;
}

/**
 * Map EmploymentTypeSelector section titles (e.g. "Agency") to the raw EmploymentType
 * values the backend filters on. An empty/full selection yields undefined (no filter).
 */
export function sectionsToEmploymentTypes(selectedSections: ReadonlySet<string>): EmploymentType[] | undefined {
	if (selectedSections.size === 0 || selectedSections.size === TEAM_SECTIONS.length) return undefined;
	const types: EmploymentType[] = [];
	for (const section of TEAM_SECTIONS) {
		if (selectedSections.has(section.title)) types.push(...section.employmentTypes);
	}
	return types.length > 0 ? types : undefined;
}
